// Precondition: Shadow DOM available
// Postcondition: Renders editorial-style company card with collapsible sections and tabbed content
// Invariant: Card state (expanded/collapsed) persists across re-renders

import { icon } from '../../../shared/icons.js';
import { fetchSubmissions } from '../../lib/financeapis.js';
import { createLogger } from '../../lib/logger.js';
import {
  parseCompanyData,
  parseEdgarSubmissions,
  getLatestQuarter,
} from '../../lib/parsers.js';
import { augmentSubmissions } from '../../lib/submission-metrics.js';

const log = createLogger('ft-company-card');

type CardState = 'idle' | 'loading' | 'error' | 'empty';

interface CompanyData {
  name: string;
  location: string;
  quarter: string;
  lede: string;
  yearsFilng: string;
  firstFiledYear: string;
  latestQuarterLabel: string;
  cik: string;
  entityType: string;
  sic: string;
  category: string;
  stateOfInc: string;
  fiscalYearEnd: string;
  ein: string;
  tickers: string;
  exchanges: string;
  insiderTxFlags: string;
  website: string;
  investorRelations: string;
  businessAddress: string;
  mailingAddress: string;
  phone: string;
  formerNames: Array<{ name: string; until: string }>;
  filings: Array<{
    form: string;
    filingDate: string;
    reportDate: string;
    accession: string;
  }>;
}

class CompanyCard extends HTMLElement {
  private isExpanded = false;
  private data: CompanyData | null = null;
  private activeTab = "overview";
  private cardState: CardState = 'idle';
  private errorMessage: string | null = null;
  private pending = false;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  get state(): CardState {
    return this.cardState;
  }

  set state(value: CardState) {
    this.cardState = value;
    this.render();
  }

  static get observedAttributes(): string[] {
    return [
      "name",
      "location",
      "quarter",
      "lede",
      "years-filing",
      "first-filed-year",
      "latest-quarter-label",
      "cik",
      "entity-type",
      "sic",
      "category",
      "state-of-inc",
      "fiscal-year-end",
      "ein",
      "tickers",
      "exchanges",
      "insider-tx-flags",
      "website",
      "investor-relations",
      "business-address",
      "mailing-address",
      "phone",
      "former-names",
      "filings",
      "expanded",
    ];
  }

  // Precondition: Element connected to DOM
  // Postcondition: Component rendered, fetches data if cik-only mode
  connectedCallback(): void {
    log.debug('connected');
    this.loadDataFromAttributes();
    this.render();

    // Self-fetch if cik is set but name isn't (cik-only mode)
    const cik = this.getAttribute('cik');
    const name = this.getAttribute('name');
    if (cik && !name) {
      this.loadCompanyData();
    }
  }

  // Precondition: Element removed from DOM
  // Postcondition: Event listeners cleaned up
  disconnectedCallback(): void {
    log.debug('disconnected');
  }

  // Precondition: Attribute changed
  // Postcondition: Component re-renders, fetches data if cik changes in cik-only mode
  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue === newValue || !this.shadowRoot) return;

    if (name === "expanded") {
      this.isExpanded = newValue !== "false";
      this.render();
      return;
    }

    // Fetch data when cik changes and no name attribute (cik-only mode).
    // Microtask batches rapid attribute changes into single fetch.
    if (name === "cik" && newValue && !this.getAttribute('name')) {
      if (this.pending) return;
      this.pending = true;
      queueMicrotask(() => {
        this.pending = false;
        this.loadCompanyData();
      });
      return;
    }

    this.loadDataFromAttributes();
    this.render();
  }

  // Precondition: cik attribute is set
  // Postcondition: Fetches company data and populates card
  private async loadCompanyData(): Promise<void> {
    const cik = this.getAttribute('cik');
    if (!cik) return;

    log.log('loadCompanyData', { cik });
    this.state = 'loading';

    const result = await fetchSubmissions(cik);

    if (!result.ok) {
      log.error('loadCompanyData failed', { cik, error: result.error });
      this.errorMessage = 'Failed to load company data';
      this.state = 'error';
      return;
    }

    const company = parseCompanyData(result.data);
    const submissions = parseEdgarSubmissions(result.data);
    log.debug('loadCompanyData success', { cik, name: company.name });

    if (!submissions.length) {
      this.errorMessage = 'No 13F filings found';
      this.state = 'empty';
      return;
    }

    // Build location
    const bizAddr = company.addresses?.business;
    const location = bizAddr
      ? [bizAddr.city, bizAddr.stateOrCountry].filter(Boolean).join(', ')
      : '';

    // Get quarter info
    const latestQuarter = getLatestQuarter(submissions) || '';
    const [year, q] = latestQuarter.split('-');
    const subMetrics = augmentSubmissions(submissions);

    // Format fiscal year end
    let fiscalYearEnd = '';
    if (company.fiscalYearEnd) {
      const month = company.fiscalYearEnd.slice(0, 2);
      const day = company.fiscalYearEnd.slice(2);
      const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
      fiscalYearEnd = `${monthNames[parseInt(month, 10)] || month} ${parseInt(day, 10)}`;
    }

    // Format address helper
    const fmtAddr = (a: { street1?: string; street2?: string; city?: string; stateOrCountry?: string; zipCode?: string } | undefined) => {
      if (!a) return '';
      return [a.street1, a.street2, [a.city, a.stateOrCountry, a.zipCode].filter(Boolean).join(' ')]
        .filter(Boolean).join('\n');
    };

    this.data = {
      name: company.name || '',
      location,
      quarter: `${q} ${year}`,
      lede: '',
      yearsFilng: subMetrics.yearsCount.toString(),
      firstFiledYear: subMetrics.firstYear || '',
      latestQuarterLabel: subMetrics.latestQuarterLabel || '',
      cik: company.cik || '',
      entityType: company.entityType || '',
      sic: company.sic + (company.sicDescription ? ` — ${company.sicDescription}` : ''),
      category: company.category || '',
      stateOfInc: company.stateOfIncorporationDescription || company.stateOfIncorporation || '',
      fiscalYearEnd,
      ein: company.ein || '',
      tickers: (company.tickers || []).join(', '),
      exchanges: (company.exchanges || []).join(', '),
      insiderTxFlags: [
        company.insiderTransactionForOwnerExists ? 'Owner tx' : 'No owner tx',
        company.insiderTransactionForIssuerExists ? 'Issuer tx' : 'No issuer tx',
      ].join(' · '),
      website: company.website || '',
      investorRelations: company.investorWebsite || '',
      businessAddress: fmtAddr(company.addresses?.business),
      mailingAddress: fmtAddr(company.addresses?.mailing),
      phone: company.phone || '',
      formerNames: (company.formerNames || []).map((x: { name: string; to: string }) => ({
        name: x.name,
        until: new Date(x.to).getFullYear().toString(),
      })),
      filings: submissions.slice(0, 10).map((s) => ({
        form: s.form || '',
        filingDate: s.filingDate,
        reportDate: s.reportDate || '',
        accession: s.accessionNumber || '',
      })),
    };

    this.state = 'idle';
  }

  // Precondition: Attributes set on element
  // Postcondition: this.data populated with parsed values
  private loadDataFromAttributes(): void {
    this.data = {
      name: this.getAttribute("name") || "",
      location: this.getAttribute("location") || "",
      quarter: this.getAttribute("quarter") || "",
      lede: this.getAttribute("lede") || "",
      yearsFilng: this.getAttribute("years-filing") || "",
      firstFiledYear: this.getAttribute("first-filed-year") || "",
      latestQuarterLabel: this.getAttribute("latest-quarter-label") || "",
      cik: this.getAttribute("cik") || "",
      entityType: this.getAttribute("entity-type") || "",
      sic: this.getAttribute("sic") || "",
      category: this.getAttribute("category") || "",
      stateOfInc: this.getAttribute("state-of-inc") || "",
      fiscalYearEnd: this.getAttribute("fiscal-year-end") || "",
      ein: this.getAttribute("ein") || "",
      tickers: this.getAttribute("tickers") || "",
      exchanges: this.getAttribute("exchanges") || "",
      insiderTxFlags: this.getAttribute("insider-tx-flags") || "",
      website: this.getAttribute("website") || "",
      investorRelations: this.getAttribute("investor-relations") || "",
      businessAddress: this.getAttribute("business-address") || "",
      mailingAddress: this.getAttribute("mailing-address") || "",
      phone: this.getAttribute("phone") || "",
      formerNames: this.parseFormerNames(),
      filings: this.parseFilings(),
    };
  }

  // Precondition: former-names attribute is JSON array or empty
  // Postcondition: Returns parsed array of former names
  private parseFormerNames(): CompanyData["formerNames"] {
    const attr = this.getAttribute("former-names");
    if (!attr) return [];
    try {
      return JSON.parse(attr);
    } catch {
      return [];
    }
  }

  // Precondition: filings attribute is JSON array or empty
  // Postcondition: Returns parsed array of filings
  private parseFilings(): CompanyData["filings"] {
    const attr = this.getAttribute("filings");
    if (!attr) return [];
    try {
      return JSON.parse(attr);
    } catch {
      return [];
    }
  }

  // Precondition: Shadow root exists
  // Postcondition: Component DOM rendered with styles
  private render(): void {
    if (!this.shadowRoot) return;

    const style = document.createElement("style");
    style.textContent = this.getStyles();

    const wrapper = document.createElement("div");
    wrapper.className = "card-wrapper";
    wrapper.innerHTML = `
      ${this.getOverlayTemplate()}
      <article class="card${this.isExpanded ? " expanded" : ""}">
        ${this.getTemplate()}
      </article>
    `;

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(wrapper);

    this.setupEventListeners();
  }

  private getOverlayTemplate(): string {
    if (this.cardState === 'idle') return '';

    const isError = this.cardState === 'error';
    const content = this.cardState === 'loading'
      ? '<ft-spinner text="Loading company data..."></ft-spinner>'
      : this.escapeHtml(this.errorMessage || 'No data available');

    return `<div class="overlay${isError ? ' error' : ''}">${content}</div>`;
  }

  private getEmptyData(): CompanyData {
    return {
      name: '',
      location: '',
      quarter: '',
      lede: '',
      yearsFilng: '',
      firstFiledYear: '',
      latestQuarterLabel: '',
      cik: '',
      entityType: '',
      sic: '',
      category: '',
      stateOfInc: '',
      fiscalYearEnd: '',
      ein: '',
      tickers: '',
      exchanges: '',
      insiderTxFlags: '',
      website: '',
      investorRelations: '',
      businessAddress: '',
      mailingAddress: '',
      phone: '',
      formerNames: [],
      filings: [],
    };
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
        margin-bottom: 1.5rem;
        --font-sans: Inter, system-ui, sans-serif;
        --font-serif: "Playfair Display", Georgia, serif;
        --font-editorial: "Instrument Serif", Georgia, serif;
        --font-source: "Source Serif 4", Georgia, serif;
        --spring-smooth: cubic-bezier(0.3, 0.7, 0.4, 1);
        --spring-bounce: cubic-bezier(0.3, 0.7, 0.4, 1.5);
      }

      @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
          animation-duration: 0.01ms !important;
          transition-duration: 0.01ms !important;
        }
      }

      * { box-sizing: border-box; margin: 0; padding: 0; }

      .card-wrapper {
        position: relative;
      }

      .overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--panel);
        z-index: 10;
        font: 400 14px/1.5 var(--font-sans);
        color: var(--muted);
        animation: overlayIn 200ms ease-out;
      }

      @keyframes overlayIn {
        from { opacity: 0; }
      }

      .overlay.error {
        color: var(--danger, #dc2626);
      }

      @media (prefers-reduced-motion: reduce) {
        .overlay { animation: none; }
      }

      .card {
        background: var(--panel);
        border: 1px solid var(--card-border);
        width: 100%;
        position: relative;
        will-change: transform;
        transition: transform 600ms var(--spring-smooth), box-shadow 400ms ease;
      }

      .card:hover {
        transform: translateY(-2px);
        box-shadow: 0 16px 32px rgba(0,0,0,0.06);
        transition: transform 250ms var(--spring-bounce), box-shadow 250ms ease;
      }

      /* Header */
      .card-header {
        padding: 36px 40px;
        cursor: pointer;
        border-bottom: 1px solid transparent;
        transition: border-color 200ms ease;
      }

      .card.expanded .card-header {
        border-bottom-color: var(--card-border);
      }

      .header-top {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 16px;
      }

      .dateline {
        font: 400 11px/1 var(--font-sans);
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: var(--muted);
      }

      .chevron {
        display: flex;
        color: var(--muted);
        transition: transform 300ms var(--spring-smooth);
        flex-shrink: 0;
      }

      .card.expanded .chevron {
        transform: rotate(180deg);
      }

      .company-name {
        font: 500 36px/1.08 var(--font-serif);
        color: var(--text);
        letter-spacing: -0.02em;
        margin-bottom: 14px;
      }

      .lede {
        font: 400 16px/1.6 var(--font-editorial);
        font-style: italic;
        color: var(--muted);
        max-width: 540px;
      }

      /* Stats row */
      .stats-row {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 24px;
        padding: 20px 40px;
        background: var(--surface, #fafaf9);
        border-bottom: 1px solid var(--card-border);
      }

      .stat {
        text-align: center;
      }

      .stat-value {
        font: 600 24px/1 var(--font-serif);
        color: var(--text);
        letter-spacing: -0.02em;
      }

      .stat-label {
        font: 400 10px/1.3 var(--font-sans);
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--muted);
        margin-top: 8px;
      }

      .stat-sublabel {
        font: 400 12px/1.3 var(--font-sans);
        color: var(--muted);
        margin-top: 4px;
      }

      /* Collapsible content - instant toggle intentional, no animation needed.
         Users expanding want immediate access to data; chevron rotation
         provides sufficient visual feedback. */
      .card-content {
        display: none;
      }

      .card.expanded .card-content {
        display: block;
      }

      /* Tabs */
      .tabs {
        display: flex;
        gap: 0;
        border-bottom: 1px solid var(--card-border);
        background: var(--tab-bg);
      }

      .tab {
        appearance: none;
        background: transparent;
        border: none;
        border-bottom: 2px solid transparent;
        color: var(--muted);
        padding: 16px 28px;
        font: 500 12px/1 var(--font-sans);
        text-transform: uppercase;
        letter-spacing: 0.08em;
        cursor: pointer;
        transition: all 200ms ease;
        position: relative;
      }

      .tab:hover {
        color: var(--text);
        background: var(--surface);
      }

      .tab[aria-selected="true"] {
        color: var(--text);
        border-bottom-color: var(--text);
        background: var(--panel);
      }

      .tab:focus {
        outline: 2px solid var(--text);
        outline-offset: -2px;
      }

      /* Panels */
      .panels {
        padding: 32px 40px;
      }

      [role="tabpanel"] {
        display: none;
      }

      [role="tabpanel"][data-active="true"] {
        display: block;
      }

      /* Key-value grid */
      .kv-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 24px 32px;
      }

      .kv-item {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .kv-item.full-width {
        grid-column: 1 / -1;
      }

      .kv-label {
        font: 400 11px/1 var(--font-sans);
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--muted);
      }

      .kv-value {
        font: 500 15px/1.4 var(--font-source);
        color: var(--text);
      }

      .kv-value.mono {
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-weight: 400;
        font-size: 14px;
        letter-spacing: 0.02em;
      }

      .kv-value a {
        color: var(--text);
        text-decoration: underline;
        text-decoration-color: var(--divider-color);
        text-underline-offset: 3px;
        transition: text-decoration-color 200ms ease;
      }

      .kv-value a:hover {
        text-decoration-color: var(--text);
      }

      /* Address blocks */
      .address-block {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 32px;
        margin-bottom: 32px;
        padding-bottom: 32px;
        border-bottom: 1px solid var(--card-border);
      }

      .address-item h4 {
        font: 600 11px/1 var(--font-sans);
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--muted);
        margin-bottom: 12px;
      }

      .address-item address {
        font: 400 14px/1.6 var(--font-source);
        font-style: normal;
        color: var(--text);
      }

      /* Filings table */
      .filings-table {
        width: 100%;
        border-collapse: collapse;
      }

      .filings-table th {
        font: 500 10px/1 var(--font-sans);
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--muted);
        text-align: left;
        padding: 0 0 16px;
        border-bottom: 1px solid var(--card-border);
      }

      .filings-table td {
        font: 400 14px/1.4 var(--font-source);
        color: var(--text);
        padding: 16px 0;
        border-bottom: 1px solid var(--divider-color);
      }

      .filings-table tr:last-child td {
        border-bottom: none;
      }

      .filings-table .form-cell {
        font: 600 13px/1 var(--font-sans);
        color: var(--text);
      }

      .filings-table .mono {
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 12px;
        color: var(--muted);
      }

      /* Former names list */
      .former-names-list {
        list-style: none;
        padding: 0;
        margin: 0;
      }

      .former-names-list li {
        font: 400 14px/1.5 var(--font-source);
        color: var(--muted);
        padding: 8px 0;
        border-bottom: 1px solid var(--divider-color);
      }

      .former-names-list li:last-child {
        border-bottom: none;
      }

      .former-names-list .name-date {
        font: 400 11px/1 var(--font-sans);
        color: var(--muted);
        margin-left: 8px;
      }

      /* Section dividers */
      .panel-section {
        margin-bottom: 28px;
        padding-bottom: 28px;
        border-bottom: 1px solid var(--card-border);
      }

      .panel-section:last-child {
        margin-bottom: 0;
        padding-bottom: 0;
        border-bottom: none;
      }

      .section-title {
        font: 500 13px/1 var(--font-sans);
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--text);
        margin-bottom: 20px;
      }

      /* Responsive */
      @media (max-width: 640px) {
        .card-header {
          padding: 24px 20px;
        }

        .company-name {
          font-size: 28px;
        }

        .lede {
          font-size: 14px;
        }

        .stats-row {
          padding: 20px;
          gap: 16px;
        }

        .stat-value {
          font-size: 20px;
        }

        .tabs {
          overflow-x: auto;
        }

        .tab {
          padding: 14px 20px;
          font-size: 11px;
        }

        .panels {
          padding: 24px 20px;
        }

        .kv-grid {
          grid-template-columns: 1fr;
          gap: 20px;
        }

        .address-block {
          grid-template-columns: 1fr;
          gap: 24px;
        }
      }
    `;
  }

  private getTemplate(): string {
    const d = this.data ?? this.getEmptyData();

    const dateline = [d.location, d.quarter]
      .filter(Boolean)
      .join(" · ");

    return `
      <header class="card-header" aria-expanded="${this.isExpanded}" aria-controls="card-content">
        <div class="header-top">
          <span class="dateline">${this.escapeHtml(dateline)}</span>
          <span class="chevron">${icon('chevron-down', 20)}</span>
        </div>
        <h1 class="company-name">${this.escapeHtml(d.name || '—')}</h1>
        ${d.lede ? `<p class="lede">${this.escapeHtml(d.lede)}</p>` : ""}
      </header>

      <div class="stats-row">
        <div class="stat">
          <div class="stat-value">${this.escapeHtml(d.yearsFilng || "—")}</div>
          <div class="stat-label">Years Filing</div>
          <div class="stat-sublabel">Since ${this.escapeHtml(d.firstFiledYear || "—")}</div>
        </div>
        <div class="stat">
          <div class="stat-value">${this.escapeHtml(d.latestQuarterLabel || "—")}</div>
          <div class="stat-label">Latest Report</div>
        </div>
      </div>

      <div id="card-content" class="card-content">
        <div class="tabs" role="tablist" aria-label="Company information sections">
          <button class="tab" role="tab" id="t-overview" aria-selected="${this.activeTab === "overview"}" aria-controls="p-overview">
            Overview
          </button>
          <button class="tab" role="tab" id="t-addresses" aria-selected="${this.activeTab === "addresses"}" aria-controls="p-addresses">
            Addresses
          </button>
          <button class="tab" role="tab" id="t-filings" aria-selected="${this.activeTab === "filings"}" aria-controls="p-filings">
            Recent Filings
          </button>
        </div>

        <div class="panels">
          ${this.getOverviewPanel()}
          ${this.getAddressesPanel()}
          ${this.getFilingsPanel()}
        </div>
      </div>
    `;
  }

  private getOverviewPanel(): string {
    const d = this.data ?? this.getEmptyData();

    return `
      <section role="tabpanel" id="p-overview" aria-labelledby="t-overview" data-active="${this.activeTab === "overview"}">
        <div class="panel-section">
          <h3 class="section-title">Entity Information</h3>
          <div class="kv-grid">
            <div class="kv-item">
              <span class="kv-label">CIK</span>
              <span class="kv-value mono">${this.escapeHtml(d.cik || "—")}</span>
            </div>
            <div class="kv-item">
              <span class="kv-label">Entity Type</span>
              <span class="kv-value">${this.escapeHtml(d.entityType || "—")}</span>
            </div>
            <div class="kv-item">
              <span class="kv-label">SIC</span>
              <span class="kv-value">${this.escapeHtml(d.sic || "—")}</span>
            </div>
            <div class="kv-item">
              <span class="kv-label">Category</span>
              <span class="kv-value">${this.escapeHtml(d.category || "—")}</span>
            </div>
            <div class="kv-item">
              <span class="kv-label">State of Inc.</span>
              <span class="kv-value">${this.escapeHtml(d.stateOfInc || "—")}</span>
            </div>
            <div class="kv-item">
              <span class="kv-label">Fiscal Year End</span>
              <span class="kv-value">${this.escapeHtml(d.fiscalYearEnd || "—")}</span>
            </div>
          </div>
        </div>

        <div class="panel-section">
          <h3 class="section-title">Identifiers</h3>
          <div class="kv-grid">
            <div class="kv-item">
              <span class="kv-label">EIN</span>
              <span class="kv-value mono">${this.escapeHtml(d.ein || "—")}</span>
            </div>
            <div class="kv-item">
              <span class="kv-label">Tickers</span>
              <span class="kv-value">${this.escapeHtml(d.tickers || "—")}</span>
            </div>
            <div class="kv-item">
              <span class="kv-label">Exchanges</span>
              <span class="kv-value">${this.escapeHtml(d.exchanges || "—")}</span>
            </div>
            <div class="kv-item">
              <span class="kv-label">Insider TX Flags</span>
              <span class="kv-value">${this.escapeHtml(d.insiderTxFlags || "—")}</span>
            </div>
          </div>
        </div>

        <div class="panel-section">
          <h3 class="section-title">Links</h3>
          <div class="kv-grid">
            <div class="kv-item">
              <span class="kv-label">Website</span>
              <span class="kv-value">${this.renderLink(d.website)}</span>
            </div>
            <div class="kv-item">
              <span class="kv-label">Investor Relations</span>
              <span class="kv-value">${this.renderLink(d.investorRelations)}</span>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  private getAddressesPanel(): string {
    const d = this.data ?? this.getEmptyData();

    return `
      <section role="tabpanel" id="p-addresses" aria-labelledby="t-addresses" data-active="${this.activeTab === "addresses"}">
        <div class="address-block">
          <div class="address-item">
            <h4>Business Address</h4>
            <address>${this.formatAddress(d.businessAddress)}</address>
          </div>
          <div class="address-item">
            <h4>Mailing Address</h4>
            <address>${this.formatAddress(d.mailingAddress)}</address>
          </div>
        </div>

        <div class="kv-grid">
          <div class="kv-item">
            <span class="kv-label">Phone</span>
            <span class="kv-value">${this.escapeHtml(d.phone || "—")}</span>
          </div>
        </div>

        ${d.formerNames.length > 0 ? `
          <div class="panel-section" style="margin-top: 28px; padding-top: 28px; border-top: 1px solid var(--card-border, #e8e6e3);">
            <h3 class="section-title">Former Names</h3>
            <ul class="former-names-list">
              ${d.formerNames
                .map(
                  (fn) => `
                <li>
                  ${this.escapeHtml(fn.name)}
                  ${fn.until ? `<span class="name-date">until ${this.escapeHtml(fn.until)}</span>` : ""}
                </li>
              `
                )
                .join("")}
            </ul>
          </div>
        ` : ""}
      </section>
    `;
  }

  private getFilingsPanel(): string {
    const d = this.data ?? this.getEmptyData();

    if (d.filings.length === 0) {
      return `
        <section role="tabpanel" id="p-filings" aria-labelledby="t-filings" data-active="${this.activeTab === "filings"}">
          <p class="kv-value">No filings available</p>
        </section>
      `;
    }

    return `
      <section role="tabpanel" id="p-filings" aria-labelledby="t-filings" data-active="${this.activeTab === "filings"}">
        <table class="filings-table">
          <thead>
            <tr>
              <th>Form</th>
              <th>Filing Date</th>
              <th>Report Date</th>
              <th>Accession Number</th>
            </tr>
          </thead>
          <tbody>
            ${d.filings
              .map(
                (f) => `
              <tr>
                <td class="form-cell">${this.escapeHtml(f.form)}</td>
                <td>${this.escapeHtml(f.filingDate)}</td>
                <td>${this.escapeHtml(f.reportDate)}</td>
                <td class="mono">${this.escapeHtml(f.accession)}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      </section>
    `;
  }

  // Precondition: Shadow root rendered
  // Postcondition: Click and keyboard listeners attached
  private setupEventListeners(): void {
    if (!this.shadowRoot) return;

    // Header toggle
    const header = this.shadowRoot.querySelector(".card-header");
    header?.addEventListener("click", () => this.toggleExpanded());

    // Tab switching
    const tabs = this.shadowRoot.querySelectorAll(".tab");
    tabs.forEach((tab) => {
      tab.addEventListener("click", (e: Event) => this.handleTabClick(e));
      tab.addEventListener("keydown", (e: Event) =>
        this.handleTabKeydown(e as KeyboardEvent)
      );
    });
  }

  // Precondition: Tab clicked
  // Postcondition: Active tab updated, panels visibility changed
  private handleTabClick(event: Event): void {
    const clickedTab = event.currentTarget as HTMLElement;
    const tabId = clickedTab.id.replace("t-", "");
    this.activateTab(tabId);
  }

  // Precondition: Keyboard event on tab
  // Postcondition: Focus moved to appropriate tab
  private handleTabKeydown(event: KeyboardEvent): void {
    if (!this.shadowRoot) return;

    const tabs = Array.from(this.shadowRoot.querySelectorAll(".tab"));
    const currentIndex = tabs.indexOf(event.currentTarget as HTMLElement);
    let nextIndex: number;

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        (tabs[nextIndex] as HTMLElement).focus();
        break;
      case "ArrowRight":
        event.preventDefault();
        nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        (tabs[nextIndex] as HTMLElement).focus();
        break;
      case "Home":
        event.preventDefault();
        (tabs[0] as HTMLElement).focus();
        break;
      case "End":
        event.preventDefault();
        (tabs[tabs.length - 1] as HTMLElement).focus();
        break;
    }
  }

  // Precondition: Valid tab ID
  // Postcondition: Tab and panel states updated
  private activateTab(tabId: string): void {
    if (!this.shadowRoot) return;

    this.activeTab = tabId;

    // Update tabs
    const tabs = this.shadowRoot.querySelectorAll(".tab");
    tabs.forEach((tab) => {
      const isActive = tab.id === `t-${tabId}`;
      tab.setAttribute("aria-selected", isActive.toString());
    });

    // Update panels
    const panels = this.shadowRoot.querySelectorAll('[role="tabpanel"]');
    panels.forEach((panel) => {
      const isActive = panel.id === `p-${tabId}`;
      panel.setAttribute("data-active", isActive.toString());
    });

    // Dispatch event
    this.dispatchEvent(
      new CustomEvent("tab-change", {
        detail: { tabId },
        bubbles: true,
        composed: true,
      })
    );
  }

  // Precondition: None
  // Postcondition: Card expanded state toggled
  private toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;

    const card = this.shadowRoot?.querySelector(".card");
    const header = this.shadowRoot?.querySelector(".card-header");

    card?.classList.toggle("expanded", this.isExpanded);
    header?.setAttribute("aria-expanded", this.isExpanded.toString());

    this.dispatchEvent(
      new CustomEvent("card-toggled", {
        detail: { expanded: this.isExpanded },
        bubbles: true,
        composed: true,
      })
    );
  }

  // Precondition: Untrusted string
  // Postcondition: HTML-safe string returned
  private escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  // Precondition: URL string or empty
  // Postcondition: Returns anchor element or em-dash
  private renderLink(url: string): string {
    if (!url || url === "—") return "—";

    const displayUrl = url
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .replace(/\/$/, "");

    return `<a href="${this.escapeHtml(url)}" target="_blank" rel="noopener">${this.escapeHtml(displayUrl)}</a>`;
  }

  // Precondition: Address string (possibly with \n or <br>)
  // Postcondition: Formatted HTML address
  private formatAddress(address: string): string {
    if (!address) return "—";
    return this.escapeHtml(address).replace(/\\n|<br\s*\/?>/gi, "<br>");
  }

  // Public API

  // Precondition: Card is collapsed
  // Postcondition: Card is expanded
  expand(): void {
    if (!this.isExpanded) {
      this.toggleExpanded();
    }
  }

  // Precondition: Card is expanded
  // Postcondition: Card is collapsed
  collapse(): void {
    if (this.isExpanded) {
      this.toggleExpanded();
    }
  }

  // Precondition: Valid tab ID (overview, addresses, filings)
  // Postcondition: Specified tab is active
  setActiveTab(tabId: string): void {
    this.activateTab(tabId);
  }
}

// Guard against duplicate registration
if (!customElements.get("ft-company-card")) {
  customElements.define("ft-company-card", CompanyCard);
}
